import React, { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import DefaultLayout from "../../components/DefaultLayout";
import axiosInstance from "../../utils/axiosInstance";
import TokenTransferModal from "../../components/TokenTransferModal";

import { showNotification } from "../../utils/error";

const SubscriptionWalletDetails = () => {
  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTokenName, setModalTokenName] = useState('ETH');
  const [modalAmount, setModalAmount] = useState('');

  const { wallet_address } = useParams();
  const { state } = useLocation();
  const subscription = state?.subscription;
  const [payments, setPayments] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(true);
  const [error, setError] = useState(null);
  const [tokenTransferLoading, setTokenTransferLoading] = useState(false);
  const [tokenRecords, setTokenRecords] = useState([]);
  const [loadingTokenRecords, setLoadingTokenRecords] = useState(true);
  const [tokenRecordsError, setTokenRecordsError] = useState(null);

  // Token Transfer handler
  const handleTokenTransfer = () => {
    setModalOpen(true);
  };

  // Fetch token records (move this above handleModalSubmit for hoisting)
  async function fetchTokenRecords() {
    setLoadingTokenRecords(true);
    setTokenRecordsError(null);
    try {
      const response = await axiosInstance.get(`/token-records/${wallet_address}`);
      if (response?.data?.success && Array.isArray(response.data.data)) {
        setTokenRecords(response.data.data);
      } else {
        setTokenRecords([]);
      }
    } catch (err) {
      setTokenRecordsError("Failed to fetch token transfer history.");
      setTokenRecords([]);
    } finally {
      setLoadingTokenRecords(false);
    }
  }

  // Modal submit handler
  const handleModalSubmit = async () => {
    if (!subscription?.wallet_address) {
      showNotification("error", "No wallet address found.");
      return;
    }
    setTokenTransferLoading(true);
    try {
      const payload = {
        wallet_id: subscription.wallet_address,
        token_name: modalTokenName,
        amount: modalAmount
      };
      const response = await axiosInstance.post("/token-records", payload);
      if (response?.data?.success) {
        showNotification("success", "Token transfer recorded successfully.");
        setModalOpen(false);
        setModalTokenName('ETH');
        setModalAmount('');
        fetchTokenRecords(); // Refresh the token transfer table
      } else {
        showNotification("error", response?.data?.message || "Token transfer failed.");
      }
    } catch (err) {
      showNotification("error", err?.response?.data?.message || err.message || "Token transfer failed.");
    } finally {
      setTokenTransferLoading(false);
    }
  };

  useEffect(() => {
    async function fetchPayments() {
      setLoadingPayments(true);
      setError(null);
      try {
        const response = await axiosInstance.get(`/wallet/${wallet_address}/payments?size=10&offset=0`);
        if (response?.data?.success && Array.isArray(response.data.data?.payments)) {
          setPayments(response.data.data.payments);
        } else {
          setPayments([]);
        }
      } catch (err) {
        setError("Failed to fetch payment history.");
        setPayments([]);
      } finally {
        setLoadingPayments(false);
      }
    }
    async function fetchTokenRecords() {
      setLoadingTokenRecords(true);
      setTokenRecordsError(null);
      try {
        const response = await axiosInstance.get(`/token-records/${wallet_address}`);
        if (response?.data?.success && Array.isArray(response.data.data)) {
          setTokenRecords(response.data.data);
        } else {
          setTokenRecords([]);
        }
      } catch (err) {
        setTokenRecordsError("Failed to fetch token transfer history.");
        setTokenRecords([]);
      } finally {
        setLoadingTokenRecords(false);
      }
    }
    if (wallet_address) {
      fetchPayments();
      fetchTokenRecords();
    }
  }, [wallet_address]);

  if (!subscription) {
    return (
      <DefaultLayout>
        <div className="text-center py-20 text-lg">No subscription data found. Please return to the subscription list and select a wallet.</div>
      </DefaultLayout>
    );
  }

  return (
    <DefaultLayout>
      <div className="w-full mt-4">
        <div className="w-full mt-4 px-2 md:px-0">
        <div className="bg-[#1E1E1E] rounded-2xl shadow-lg border border-[#313D4F] p-8">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl font-bold text-[#D9D9D9]">Subscription Details</h2>
             <button
               className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-bold py-2 px-6 rounded-full shadow-lg transition duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
               type="button"
               onClick={handleTokenTransfer}
               disabled={tokenTransferLoading}
             >
               Token Transfer
             </button>
             <TokenTransferModal
               open={modalOpen}
               onClose={() => setModalOpen(false)}
               onSubmit={handleModalSubmit}
               loading={tokenTransferLoading}
               tokenName={modalTokenName}
               setTokenName={setModalTokenName}
               amount={modalAmount}
               setAmount={setModalAmount}
             />
          </div>
          {/* Subscription Details Table */}
          <table className="w-full mb-10 bg-[#232323] rounded-xl overflow-hidden">
            <tbody>
              <tr>
                <td className="py-3 px-4 text-[#A0AEC0] font-semibold w-1/3">Wallet Address</td>
                <td className="py-3 px-4 break-all text-[#F3F3F3]">{subscription.wallet_address}</td>
              </tr>
              <tr>
                <td className="py-3 px-4 text-[#A0AEC0] font-semibold">Email</td>
                <td className="py-3 px-4 text-[#F3F3F3]">{subscription.email}</td>
              </tr>
              <tr>
                <td className="py-3 px-4 text-[#A0AEC0] font-semibold">Plan</td>
                <td className="py-3 px-4 text-[#F3F3F3]">{subscription.plan|| '-'}</td>
              </tr>
              <tr>
                <td className="py-3 px-4 text-[#A0AEC0] font-semibold">Amount</td>
                <td className="py-3 px-4 text-[#F3F3F3]">{(subscription.amount ?? subscription.plan?.amount) ? `${subscription.amount ?? subscription.plan?.amount}$` : '-'}</td>
              </tr>
              <tr>
                <td className="py-3 px-4 text-[#A0AEC0] font-semibold">Status</td>
                <td className="py-3 px-4">
                  <span className={`inline-block px-4 py-1 rounded-full text-xs font-bold tracking-wide ${subscription.subscription_status === 'ACTIVE' ? 'bg-green-900/50 text-green-400' : 'bg-yellow-900/50 text-yellow-400'}`}>{subscription.subscription_status === 'APPROVAL_PENDING' ? 'Pending' : subscription.subscription_status}</span>
                </td>
              </tr>
              <tr>
                <td className="py-3 px-4 text-[#A0AEC0] font-semibold">Next Payment Date</td>
                <td className="py-3 px-4 text-[#F3F3F3]">{new Date(subscription.next_payment_date).toLocaleDateString()}</td>
              </tr>
              <tr>
                <td className="py-3 px-4 text-[#A0AEC0] font-semibold">Created At</td>
                <td className="py-3 px-4 text-[#F3F3F3]">{new Date(subscription.created_at).toLocaleDateString()}</td>
              </tr>
            </tbody>
          </table>

          {/* Payment History Table */}
          <h3 className="text-xl font-bold text-[#D9D9D9] mb-4 mt-8 text-center">Payment History</h3>
          <table className="w-full bg-[#232323] rounded-xl overflow-hidden">
            <thead className="bg-[#353535]">
              <tr>
                <th className="px-4 py-2 text-left text-[#A0AEC0] font-semibold">Date</th>
                <th className="px-4 py-2 text-left text-[#A0AEC0] font-semibold">Amount</th>
                <th className="px-4 py-2 text-left text-[#A0AEC0] font-semibold">Plan</th>
                <th className="px-4 py-2 text-left text-[#A0AEC0] font-semibold">Method</th>
                <th className="px-4 py-2 text-left text-[#A0AEC0] font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {loadingPayments ? (
                <tr>
                  <td colSpan="5" className="text-center py-6 text-[#A0AEC0]">Loading payment history...</td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan="5" className="text-center py-6 text-[#FF6B6B]">{error}</td>
                </tr>
              ) : payments.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center py-6 text-[#A0AEC0]">Payment history is not available.</td>
                </tr>
              ) : (
                payments.map((payment) => {
                  let dateString = '-';
                  if (payment.created_at) {
                    const d = new Date(payment.created_at);
                    if (!isNaN(d)) dateString = d.toLocaleDateString();
                  }
                  return (
                    <tr key={payment.transaction_id}>
                      <td className="py-3 px-4 text-[#F3F3F3]">{dateString}</td>
                      <td className="py-3 px-4 text-[#F3F3F3]">{payment.amount}$</td>
                      <td className="py-3 px-4 text-[#F3F3F3]">{payment.tier?.name || '-'}</td>
                      <td className="py-3 px-4 text-[#F3F3F3]">{payment.payment_method || '-'}</td>
                      <td className="py-3 px-4">
                        <span className={`inline-block px-4 py-1 rounded-full text-xs font-bold tracking-wide ${
                          (payment.status || 'COMPLETED') === 'COMPLETED'
                            ? 'bg-green-900/50 text-green-400'
                            : (payment.status || 'COMPLETED') === 'REVERSED'
                            ? 'bg-yellow-900/50 text-yellow-400'
                            : (payment.status || 'COMPLETED') === 'PENDING'
                            ? 'bg-blue-900/50 text-blue-400'
                            : 'bg-red-900/50 text-red-400'
                        }`}>
                          {payment.status || 'COMPLETED'}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>

          {/* Token Transfer History Table */}
          <h3 className="text-xl font-bold text-[#D9D9D9] mb-4 mt-8 text-center">Token Transfer History</h3>
          <table className="w-full bg-[#232323] rounded-xl overflow-hidden">
            <thead className="bg-[#353535]">
              <tr>
                <th className="px-4 py-2 text-left text-[#A0AEC0] font-semibold">Month</th>
                <th className="px-4 py-2 text-left text-[#A0AEC0] font-semibold">Date</th>
                <th className="px-4 py-2 text-left text-[#A0AEC0] font-semibold">Token</th>
                <th className="px-4 py-2 text-left text-[#A0AEC0] font-semibold">Amount</th>
              </tr>
            </thead>
            <tbody>
              {loadingTokenRecords ? (
                <tr>
                  <td colSpan="3" className="text-center py-6 text-[#A0AEC0]">Loading token transfer history...</td>
                </tr>
              ) : tokenRecordsError ? (
                <tr>
                  <td colSpan="3" className="text-center py-6 text-[#FF6B6B]">{tokenRecordsError}</td>
                </tr>
              ) : tokenRecords.length === 0 ? (
                <tr>
                  <td colSpan="3" className="text-center py-6 text-[#A0AEC0]">Token transfer history is not available.</td>
                </tr>
              ) : (
                tokenRecords.map((record) => {
                  const dateObj = new Date(record.date);
                  const month = dateObj.toLocaleString('default', { month: 'long' });
                  const date = dateObj.toLocaleDateString();
                  return (
                    <tr key={record.id}>
                      <td className="py-3 px-4 text-[#F3F3F3]">{month}</td>
                      <td className="py-3 px-4 text-[#F3F3F3]">{date}</td>
                      <td className="py-3 px-4 text-[#F3F3F3]">{record.token_name}</td>
                      <td className="py-3 px-4 text-[#F3F3F3]">{record.amount}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
    </DefaultLayout>
  );
};

export default SubscriptionWalletDetails;
